/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { ISchemaConstPasswordModel, TGenericModelNode } from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";
import { FieldText } from "../../Fields/FieldText";

export const ControlConstPassword_Primary: TInspectorControlComponent<ISchemaConstPasswordModel> = ({
	model: _model,
	id
}) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constPassword");

	const inspectorContext = useEditorInspectorContext();

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldText
			id={id}
			type="password"
			value={model.value}
			onChange={handleChange}
			validationErrors={validationErrors}
			grow={true}
			readOnly={inspectorContext?.readOnly}
			debounceTimeout={inspectorContext?.inputDebounceTimeout}
			minLength={model.schema.opts.constraints?.min}
			maxLength={model.schema.opts.constraints?.max}
		/>
	);
};

export const ControlConstPassword = (model: TGenericModelNode): IInspectorControls => ({
	primary: {
		component: ControlConstPassword_Primary,
		model: model
	}
});
